import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { Component, OnInit, OnDestroy, NgZone } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Observable, map } from 'rxjs';
import { filter, first } from 'rxjs/operators';
import { FirestoreService } from 'src/app/shared/services/firestore.service';
import { Auth, onAuthStateChanged, Unsubscribe } from '@angular/fire/auth';
import {
  Firestore,
  doc,
  getDoc,
  updateDoc,
  collection,
  query,
  where,
  getDocs,
  Timestamp,
  onSnapshot,
} from '@angular/fire/firestore';
import { UxserviceService } from 'src/app/shared/services/uxservice.service';
import { NotificationService } from 'src/app/shared/services/notification.service';
import { BookingCounterService } from 'src/app/shared/services/booking-counter.service';
import { SubscriptionPlanService, AdminPlan } from 'src/app/subscription-management/subscription-services/subscription-plan.service';
import { environment } from 'src/app/environments/environment';


interface UserDetails {
  uid: string;
}

interface UserProfile {
  companyname: string;
  role?: string;
  photoURL?: string;
  accountdetails?: {
    companyname?: string;
  };
  uid?: string;
  plan?: string;
  planId?: string;  // ← ADD THIS

  planName?: string;
  subscriptionExpiry?: string;
}

interface MenuItem {
  isHeader?: boolean;
  label: string;
  icon?: string;
  route?: string;
  active?: boolean;
  badge?: number;
  isPremium?: boolean;
}
interface SidebarPlanData {
  name: string;
  color: string;
  marketplaceEnabled: boolean;
  activeListingLimit: number;
  activeListings: number;
  renewalDate: Date | null;
  bookingLimit: number;
  storageLimitGb: number;
  listingFee: number;
  addOnBookingEnabled: boolean;
  monthlyFee: number;
  term: string;
}

interface UsageData {
  bookingsUsed: number;
  bookingsRemaining: number;
  daysUntilRenewal: number;
  totalVehicles: number;
  storageUsedGb: number;
}
@Component({
  selector: 'app-businessuser-layout',
  templateUrl: './businessuser-layout.component.html',
  styleUrls: ['./businessuser-layout.component.scss'],
})
export class BusinessuserLayoutComponent implements OnInit, OnDestroy {
  userdetail!: UserDetails;
  appVersion = environment.version;
  currentuid: string | null = null;
  userObj: UserProfile | null = null;
  currentuserrole: string = '';
  currentuserprofile: string = '';
  currentusername: string = '';
  isMobileView = false;
  sidebarOpen = true;
  lang = 'en';
  today = new Date();

  // ── Plan badge data (for profilecard in the top-bar menu) ──
  planBadgeName: string = '';
  planBadgeColor: string = '';

  // plan modal
  planModalOpen = false;
  planRenewalDate: Date | null = null;
  sidebarPlanData: SidebarPlanData | null = null;
  allPlans: AdminPlan[] = [];
  currentPlanId: string = '';
  usageData: UsageData = {
    bookingsUsed: 0, bookingsRemaining: 0,
    daysUntilRenewal: 0, totalVehicles: 0, storageUsedGb: 0
  };

  menuItems: MenuItem[] = [
    {
      label: 'Vendor Dashboard',
      icon: 'fa-solid fa-chart-line',
      route: '/business/Dashboard',
      active: true,
    },

    { isHeader: true, label: 'QUICK ACTIONS' },
    {
      label: 'Add Equipment',
      icon: 'fa-solid fa-plus-square',
      route: '/business/equipment/add',
    },
    {
      label: 'Manage Equipment',
      icon: 'fa-solid fa-truck-moving',
      route: '/business/equipment',
    },
    {
      label: 'Listed Equipments',
      icon: 'fa-solid fa-list-check',
      route: '/business/listed-equipment',
    },
    {
      label: 'Booking Requests',
      icon: 'fa-solid fa-comments',
      route: '/business/booking',
    },
    {
      label: 'Notifications',
      icon: 'fa-solid fa-bell',
      route: '/business/notifications',
    },
    {
      label: 'Customer Directory',
      icon: 'fa-solid fa-address-book',
      route: '/business/customer-history',
    },
    {
      label: 'Drive',
      icon: 'fa-solid fa-cloud',
      route: '/business/drive',
      isPremium: true,
    },

    { isHeader: true, label: 'DIRECT LINKS' },
    {
      label: 'Take a Tour',
      icon: 'fa-solid fa-circle-play',
      route: '/business/tutorial',
    },
    { label: 'Back to Home', icon: 'fa-solid fa-home', route: '/' },
  ];

  openVehiclePlanDialog(): void {
    this.router.navigate(['/business/vehicle-plan']);
  }

  companyname: string | null = null;

  notificationOpen = false;
  unreadNotificationCount$: Observable<number>;
  notifications$: Observable<any[]>;

  private authUnsubscribe?: Unsubscribe;

  constructor(
    private firestoreService: FirestoreService,
    private router: Router,
    private translateService: TranslateService,
    private breakpointObserver: BreakpointObserver,
    private firestore: Firestore,
    private auth: Auth,
    private ux: UxserviceService,
    public notificationService: NotificationService,
    private bookingCounterService: BookingCounterService,
    private ngZone: NgZone,
    private planService: SubscriptionPlanService,
  ) {
    this.notifications$ = this.notificationService.notifications$.pipe(
      map((notes) => {
        const sevenDaysAgo = new Date();
        sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
        return notes.filter((note) => {
          if (!note.createdAt) return false;
          const date = note.createdAt.toDate
            ? note.createdAt.toDate()
            : new Date(note.createdAt);
          return date >= sevenDaysAgo;
        });
      }),
    );
    this.unreadNotificationCount$ = this.notifications$.pipe(
      map((notes) => notes.filter((n) => !n.isRead).length),
    );
  }

  tutorialStep = 0;
  realPendingCount = 0;

  ngOnInit(): void {
    this.bookingCounterService.pendingCount$.subscribe((count) => {
      this.realPendingCount = count;
      this.updateBadge();
    });

    this.lang = localStorage.getItem('lang') || 'en';
    this.translateService.use(this.lang);

    this.initAuthState();

    this.breakpointObserver
      .observe([Breakpoints.Handset])
      .subscribe((result) => {
        this.isMobileView = result.matches;
        this.sidebarOpen = !result.matches;
      });

    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe(() => {
        if (this.isMobileView) this.sidebarOpen = false;
        this.updateActiveMenu();

        // Ensure page scrolls to top when navigating to any view
        const scrollAllToTop = () => {
          window.scrollTo(0, 0);
          document.documentElement.scrollTop = 0;
          document.body.scrollTop = 0;
          const mainArea = document.querySelector('.main-content-area');
          if (mainArea) {
            mainArea.scrollTop = 0;
          }
        };
        scrollAllToTop();
        setTimeout(() => scrollAllToTop(), 50);

        if (this.router.url.startsWith('/business/booking')) {
          this.markBookingsAsSeen();
        }
      });

    this.updateActiveMenu();

    document.addEventListener('click', this.onDocumentClick);
  }

  updateBadge() {
    const bookingItem = this.menuItems.find(
      (item) => item.label === 'Booking Requests',
    );
    if (!bookingItem) return;

    const lastSeen = parseInt(
      localStorage.getItem('seenBookingCount') || '0',
      10,
    );

    let display = this.realPendingCount - lastSeen;
    if (display < 0) display = 0;

    bookingItem.badge = display;
  }

  markBookingsAsSeen() {
    localStorage.setItem('seenBookingCount', this.realPendingCount.toString());
    this.updateBadge();
  }

  private userProfileUnsub?: Unsubscribe;

  ngOnDestroy() {
    document.removeEventListener('click', this.onDocumentClick);
    if (this.authUnsubscribe) this.authUnsubscribe();
    if (this.userProfileUnsub) this.userProfileUnsub();
  }

  onDocumentClick = () => {
    this.notificationOpen = false;
  };

  updateActiveMenu() {
    const currentUrl = this.router.url;

    this.menuItems.forEach((item) => (item.active = false));

    const matchingItems = this.menuItems.filter(
      (item) =>
        item.route &&
        (currentUrl === item.route ||
          (item.route !== '/' && currentUrl.startsWith(item.route))),
    );

    if (matchingItems.length > 0) {
      const bestMatch = matchingItems.sort(
        (a, b) => b.route!.length - a.route!.length,
      )[0];
      bestMatch.active = true;
    }
  }

  // ── Load plan name/color for the topbar profile badge ──────────────────

  initAuthState() {
    this.authUnsubscribe = onAuthStateChanged(this.auth, (user) => {
      if (user) {
        this.currentuid = user.uid;

        this.ux.vendorTutorialStep$.subscribe((step) => {
          this.tutorialStep = step;
        });

        if (this.userProfileUnsub) {
          this.userProfileUnsub();
        }

        const userDocRef = doc(this.firestore, 'users-list', user.uid);
        this.userProfileUnsub = onSnapshot(userDocRef, async (snap) => {
          this.ngZone.run(async () => {
            const rawProfile = snap.exists() ? snap.data() as any : null;
            if (!rawProfile) return;

            // 🚫 Real-time check: if vendor account is disabled, eject to home with message
            if (rawProfile.role === 'business' && rawProfile.accountStatus === 'disabled') {
              this.ux.showMessage(
                'Your vendor account has been disabled by administration. Access to the Vendor Dashboard is restricted. Please contact support.',
                'Account Disabled',
                'error'
              );
              this.router.navigate(['/']);
              return;
            }

            // ── WORKER SUPPORT: resolve effective profile ────────────────
            // If the logged-in user is a worker, load the parent vendor's
            // profile instead so the dashboard shows the vendor's company
            // name, logo, and subscription details.
            let profile: UserProfile;
            let effectiveUid: string;

            if (rawProfile.role === 'worker' && rawProfile.parentUid) {
              effectiveUid = rawProfile.parentUid;
              const parentSnap = await getDoc(doc(this.firestore, 'users-list', rawProfile.parentUid));
              if (parentSnap.exists() && parentSnap.data()?.['accountStatus'] === 'disabled') {
                this.ux.showMessage(
                  'Your company account has been disabled by administration. Access to the Vendor Dashboard is restricted. Please contact support.',
                  'Account Disabled',
                  'error'
                );
                this.router.navigate(['/']);
                return;
              }
              profile = parentSnap.exists() ? { ...(parentSnap.data() as UserProfile), uid: rawProfile.parentUid } : rawProfile;
            } else {
              effectiveUid = user.uid;
              profile = { ...rawProfile, uid: user.uid };
            }
            // ── END WORKER SUPPORT ───────────────────────────────────────

            this.userObj = profile;
            this.currentuserrole = rawProfile.role ?? '';
            this.currentuserprofile = profile.photoURL ?? '';
            this.currentusername =
              profile.companyname ||
              (profile as any).companyName ||
              profile.accountdetails?.companyname ||
              rawProfile.firstname ||
              'User';

            this.loadPlanBadge(profile);
          });
        }, (err) => {
          console.error('Error listening to user profile changes:', err);
        });

        this.checkEquipmentCount();
      } else {
        this.currentuid = '';
        this.userObj = null;
        this.ux.vendorTutorialStep$.next(0);
        if (this.userProfileUnsub) {
          this.userProfileUnsub();
          this.userProfileUnsub = undefined;
        }
      }
    });
  }

  /**
   * Redirects a new user (no plan) to the plan-selection page.
   * Kept as a named method so other parts of the layout can call it
   * if they ever detect a missing-plan state at runtime.
   */
  openMandatorySubscriptionPopup(): void {
    this.router.navigate(['/business/vehicle-plan']);
  }

  async checkEquipmentCount() {
    if (!this.currentuid) return;
    try {
      // Use the effective vendor UID so workers don't incorrectly trigger the tutorial
      const userSnap = await getDoc(doc(this.firestore, 'users-list', this.currentuid!));
      let effectiveUid = this.currentuid;
      if (userSnap.exists()) {
        const data = userSnap.data() as any;
        if (data.role === 'worker' && data.parentUid) {
          effectiveUid = data.parentUid;
        }
      }

      const equipRef = collection(this.firestore, 'equipment-list');
      const q = query(equipRef, where('bywhom', '==', effectiveUid));
      const equipSnap = await getDocs(q);

      if (equipSnap.size === 0) {
        if (this.tutorialStep === 0) {
          this.ux.vendorTutorialStep$.next(1);
        }
      }
    } catch (error) {
      console.error('Error checking equipment count:', error);
    }
  }

  nextTutorialStep(event: Event) {
    event.stopPropagation();
    this.ux.vendorTutorialStep$.next(this.tutorialStep + 1);
  }

  prevTutorialStep(event: Event) {
    event.stopPropagation();
    this.ux.vendorTutorialStep$.next(this.tutorialStep - 1);
  }

  finishTutorial(event: Event) {
    event.stopPropagation();
    this.ux.vendorTutorialStep$.next(0);
    this.router.navigate(['/business/Dashboard']);
  }

  closeTutorial(event: Event) {
    event.stopPropagation();
    this.ux.vendorTutorialStep$.next(0);
  }

  rewatchTutorial(event: Event) {
    event.stopPropagation();
    this.router.navigate(['/business/tutorial']);
  }

  ChangeLang(lang: any) {
    const selectedLanguage = lang.target.value;
    this.lang = selectedLanguage;
    localStorage.setItem('lang', selectedLanguage);
    this.translateService.use(selectedLanguage);
  }

  toggleSidebar() {
    this.sidebarOpen = !this.sidebarOpen;
  }

  navigateTo(item: MenuItem) {
    if (item.isHeader || !item.route) return;
    if (this.tutorialStep > 0) return;

    // Helper to scroll page and main content area to top
    const scrollToTop = () => {
      window.scrollTo(0, 0);
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
      const mainArea = document.querySelector('.main-content-area');
      if (mainArea) {
        mainArea.scrollTop = 0;
      }
    };

    // If the user clicks the menu item they are already on, scroll to top so header/buttons are in view
    if (this.router.url === item.route || this.router.url.startsWith(item.route + '?')) {
      scrollToTop();
      return;
    }

    this.menuItems.forEach((menuItem) => (menuItem.active = false));
    item.active = true;
    scrollToTop();
    this.router.navigate([item.route]);
  }

  toggleNotification(event: MouseEvent) {
    event.stopPropagation();
    this.notificationOpen = !this.notificationOpen;

    if (this.notificationOpen && this.currentuid) {
      this.notificationService.markAllAsRead(this.currentuid);
    }
  }

  markAsRead(notification: any) {
    if (!this.currentuid) return;
    this.notificationService.markAsRead(this.currentuid, notification.id);
  }

  handleNotificationClick(notification: any) {
    this.markAsRead(notification);
    this.notificationOpen = false;

    this.ngZone.run(() => {
      const notifLink: string = notification.link || '';

      // If the notification carries a '/booking-orders' link, the current user
      // was the CUSTOMER in that booking (e.g. vendor who booked another vendor's
      // vehicle). Route to the customer order-details page in that case.
      if (notifLink.startsWith('/booking-orders')) {
        if (notification.bookingId) {
          this.router.navigate(['/booking-orders', notification.bookingId]);
        } else {
          this.router.navigate(['/booking-orders']);
        }
      } else {
        // Normal vendor notification — go to vendor booking-details
        if (notification.bookingId) {
          this.router.navigate([
            '/business/booking/booking-details',
            notification.bookingId,
          ]);
        } else {
          this.router.navigate(['/business/booking']);
        }
      }
    });
  }

  viewAllNotifications() {
    this.notificationOpen = false;
    this.router.navigate(['/business/notifications']);
  }

  clearAllNotifications() {
    if (this.currentuid) {
      if (confirm('Are you sure you want to clear all notifications?')) {
        this.notificationService.clearAllNotifications(this.currentuid);
      }
    }
  }

  openPlanModal(): void { this.planModalOpen = true; }
  closePlanModal(): void { this.planModalOpen = false; }

  navigateToUpgrade(): void {
    this.closePlanModal();
    this.router.navigate(['/business/vehicle-plan']);
  }

  navigateToInvoices(): void {
    this.closePlanModal();
    this.router.navigate(['/business/invoices']);
  }

  loadPlanBadge(profile: UserProfile): void {
    // Accept profiles that have either the new planId or the legacy plan field
    if (!profile?.plan && !profile?.planId) return;
    this.currentPlanId = (profile.planId ?? profile.plan) as string;

    this.planService.plans$
      .pipe(filter(p => p.length > 0), first())
      .subscribe(async (plans) => {
        this.allPlans = plans;

        // Match by planId (Firestore doc ID) first, fall back to plan name/id
        let matched = plans.find(p => p.id === profile.planId);
        if (!matched && profile.plan) {
          // Legacy: plan field may hold the Firestore doc ID or human name
          matched = plans.find(p => p.id === profile.plan || p.name === profile.plan);
        }
        if (!matched) return;

        this.planBadgeName = matched.name ?? '';
        this.planBadgeColor = '#3bb7e8';

        const rawExpiry = profile.subscriptionExpiry as any;
        let renewalDate: Date | null = null;
        if (rawExpiry) {
          if (typeof rawExpiry.toDate === 'function') {
            renewalDate = rawExpiry.toDate();
          } else {
            renewalDate = new Date(rawExpiry);
          }
        }
        this.planRenewalDate = renewalDate;

        // active listings count
        let activeListings = 0;
        if (matched.marketplaceEnabled && profile.uid) {
          try {
            const q = query(
              collection(this.firestore, 'listings'),
              where('vendorId', '==', profile.uid),
              where('isMarketplaceActive', '==', true),
            );
            const snap = await getDocs(q);
            activeListings = snap.docs.filter(d => d.data()['isMarketplaceActive'] === true).length;
          } catch { activeListings = 0; }
        }

        // total vehicles
        let totalVehicles = 0;
        if (profile.uid) {
          try {
            const vq = query(
              collection(this.firestore, 'equipment-list'),
              where('bywhom', '==', profile.uid),
            );
            totalVehicles = (await getDocs(vq)).size;
          } catch { totalVehicles = 0; }
        }

        const bookingsUsed = (profile as any).manualBookingsUsed ?? 0;
        const bookingLimit = matched.bookingLimit === 0 ? 999 : matched.bookingLimit;
        const daysUntilRenewal = renewalDate
          ? Math.max(0, Math.ceil((renewalDate.getTime() - Date.now()) / 86400000)) : 0;

        this.sidebarPlanData = {
          name: matched.name,
          color: matched.color ?? '#0ea5e9',
          marketplaceEnabled: matched.marketplaceEnabled ?? false,
          activeListingLimit: matched.activeListingLimit ?? 0,
          activeListings,
          renewalDate,
          bookingLimit: matched.bookingLimit ?? 0,
          storageLimitGb: matched.storageLimitGb ?? 0,
          listingFee: matched.listingFee ?? 0,
          addOnBookingEnabled: matched.addOnBookingEnabled ?? false,
          monthlyFee: matched.monthlyFee ?? 0,
          term: (profile as any).term ?? 'monthly',
        };

        this.usageData = {
          bookingsUsed,
          bookingsRemaining: Math.max(0, bookingLimit - bookingsUsed),
          daysUntilRenewal,
          totalVehicles,
          storageUsedGb: 0,
        };
      });
  }
}